import React, { forwardRef, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import sleep from 'utils/sleep';
import FamilyTree from './balkangraph/familytree';
import { getImagePreview } from '../lib/imageTransformer';

const transformPersonToGraph = (data, personId) => {
  return {
    ...data,
    personId: data.personId ?? personId,
    fullName: [data.first, data.last].join(' '),
  };
};

const transformPersonToApi = data => {
  return data;
};

const formatDateObject = ({ d, m, y } = {}) => {
  if (y) {
    return y;
  }

  return undefined;
};

const Tree = forwardRef((props, ref) => {
  const divRef = useRef();
  const familyRef = useRef();

  useEffect(() => {
    familyRef.current = new FamilyTree(divRef.current, {
      mouseScrool: FamilyTree.action.zoom,
      layout: FamilyTree.layout.normal, // don't use tree
      // min: true,
      template: 'custom',
      nodeTreeMenu: true,
      nodeBinding: {
        field_0: 'first',
        field_1: 'last',
        field_2: 'dates',
        img_0: 'photo',
      },
      searchDisplayField: 'fullName',
      searchFields: ['fullName'],
      miniMap: true,
      toolbar: {
        layout: false,
        zoom: true,
        fit: true,
        expandAll: false,
        fullScreen: false,
      },
      nodeMouseClick: FamilyTree.action.none,
    });

    familyRef.current.on('click', (sender, args) => {
      if (args.node) {
        const isMenuOpen = !!Object.keys(familyRef.current.nodes).find(
          id => id.indexOf('_ft_') === 0,
        );
        if (isMenuOpen) {
          return false;
        }

        const id = args.node.id;
        const person = { id, pids: [], ...familyRef.current.get(id) };
        const partners = person.pids
          .map(pid => {
            const data = familyRef.current.get(pid);
            if (data) {
              return { id: pid, ...data };
            }
            return undefined;
          })
          .filter(p => !!p);

        props.onNodeClick({
          person: transformPersonToApi({ ...person, partners }),
          people: ref.current.getNodes(),
        });
        return false;
      }
      return true;
    });

    familyRef.current.onUpdateNode(({ addNodesData, updateNodesData }) => {
      if (addNodesData.length > 0) {
        props.onAddNodes(addNodesData, updateNodesData);
      }
    });

    familyRef.current.onNodeTreeMenuShow(args => {
      const isChildOfNonExistingPartner = (mid, fid) => {
        return [mid, fid].indexOf('_ft_partner') >= 0;
      };

      args.nodes = args.nodes.filter(item => {
        if (isChildOfNonExistingPartner(item.mid, item.fid)) {
          return false;
        }

        if (item._ft_childData) {
          if (isChildOfNonExistingPartner(item._ft_childData.mid, item._ft_childData.fid)) {
            return false;
          }
        }
        return true;
      });
    });

    familyRef.current.on('field', (sender, args) => {
      if (args.data) {
        if (args.name === 'dates') {
          const birthDate = formatDateObject(args.data.birthDate);
          const deathDate = formatDateObject(args.data.deathDate);

          let value = '';

          if (birthDate && deathDate) {
            value = [birthDate, deathDate].join(' - ');
          } else if (birthDate) {
            value = `${birthDate} - `;
          } else if (deathDate) {
            value = `- ${deathDate}`;
          }

          args.value = value;
        }

        if (args.name === 'photo') {
          let value = args.data.photo;
          if (!value) {
            const gender = args.data.gender;
            if (!gender) {
              value = undefined;
            } else if (gender === 'female') {
              value = '/static/img/female-avatar.png';
            } else {
              value = '/static/img/male-avatar.png';
            }
          } else {
            value = getImagePreview(value, { width: 200 });
          }
          args.value = value;
        }
      }
    });

    familyRef.current.load(props.persons.map(transformPersonToGraph));

    ref.current = {
      center: id => {
        familyRef.current.center(id);
      },
      canRemove: id => {
        const numberOfNodes = Object.keys(familyRef.current.nodes).length;
        return numberOfNodes > 1 && familyRef.current.canRemove(id);
      },
      update: (id, data) => {
        familyRef.current.updateNode(transformPersonToGraph({ id, ...data }));
      },
      delete: id => {
        familyRef.current.removeNode(id);
      },
      getNodes: (ids = null) => {
        return (ids ?? Object.keys(familyRef.current.nodes))
          .map(id => {
            const data = familyRef.current.get(id);
            if (data) {
              return { id, ...data };
            }
            return undefined;
          })
          .filter(p => !!p)
          .map(transformPersonToApi);
      },
      updatePersons: async persons => {
        // TODO hack - updateNode doesn't know this person immediatelly
        await sleep(250);

        persons.forEach(person => {
          familyRef.current.updateNode(transformPersonToGraph(person));
        });
      },
    };
  }, []);

  return (
    <>
      <div id="familytree" ref={divRef} style={{ backgroundColor: props.backgroundColor }} />
    </>
  );
});

Tree.propTypes = {
  treeId: PropTypes.number,
  backgroundColor: PropTypes.string,
  persons: PropTypes.array,
  onNodeClick: PropTypes.func,
  onAddNodes: PropTypes.func,
};

Tree.defaultProps = {
  backgroundColor: '#F6F6F6',
};

export default Tree;
